import { useApolloClient } from '@apollo/client';
import * as React from 'react';

import { useGlobalSearchContext } from '@/imports/ui/components/search/SearchProvider';

import { loadResultsState } from '../components/results/loadResultsState';
import type { AiSearchResultsPageState } from '../types';
import type { AiAskIndexData } from '../utils/aiAskHelpers';

export function useResultsPage(queryToken: string) {
  const apolloClient = useApolloClient();
  const searchClient = useGlobalSearchContext();
  const indexData = searchClient.indexData;

  const [state, setState] = React.useState<AiSearchResultsPageState | null>(
    null
  );
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!queryToken || !indexData) {
      setState(null);
      setError(queryToken ? '' : 'Missing query token.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setState(null);

    void loadResultsState(apolloClient, queryToken, indexData)
      .then((resultsState) => {
        if (!cancelled) {
          setState(resultsState);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          const errMsg = e instanceof Error ? e.message : String(e);
          setError(`Failed to load results: ${errMsg}`);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apolloClient, indexData, queryToken]);

  return {
    state,
    error,
    loading,
    indexData: indexData as AiAskIndexData | undefined,
    isSearchReady: Boolean(searchClient.client && indexData),
  };
}
